import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Card, CardContent, Grid, Tabs, Tab, Button, 
  List, ListItem, ListItemText, ListItemIcon, Chip, Dialog, 
  DialogTitle, DialogContent, DialogActions, TextField, 
  Snackbar, Alert, Avatar, Paper, Switch, FormControlLabel,
  MenuItem, IconButton, Tooltip,
  useTheme, useMediaQuery
} from '@mui/material';
import TenantAuth from './TenantAuth';
import PhotoUpload from './PhotoUpload';
import api from '../api';
import { isFinancialsEnabled } from '../config/featureFlags';
import TenantPortalLedger from './financials/TenantPortalLedger';
import {
  Home as HomeIcon,
  Build as BuildIcon,
  Payment as PaymentIcon,
  Description as LeaseIcon,
  Notifications as NotificationIcon,
  Settings as SettingsIcon,
  Add as AddIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Mail as MailIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Lock as LockIcon
} from '@mui/icons-material';
import { TENANT_PASSWORD_HINT, tenantPasswordError } from '../utils/tenantPassword';
import { PHOTO_UPLOAD_TIMEOUT_MS, photoFormData } from '../utils/photoUpload';
import {
  TENANT_SESSION_ENDED_EVENT,
  clearTenantSession,
  getTenantSession,
  tenantDisplayName
} from '../utils/tenantSession';

const canTenantChangePayment = (payment) => payment?.status === 'pending' && payment?.source === 'tenant_portal';
const canTenantChangeRequest = (request) => request?.status === 'pending';

const TenantPortal = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [activeTab, setActiveTab] = useState(0);
  const [tenant, setTenant] = useState(null);
  const [maintenanceRequests, setMaintenanceRequests] = useState([]);
  const [payments, setPayments] = useState([]);
  const [lease, setLease] = useState(null);
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [notifications, setNotifications] = useState([]);
  const [messages, setMessages] = useState([]);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [messageDialog, setMessageDialog] = useState(false);
  const [notificationPreferences, setNotificationPreferences] = useState({
    emailNotifications: true,
    smsNotifications: false,
    maintenanceUpdates: true,
    paymentReminders: true
  });
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [restoringSession, setRestoringSession] = useState(() => Boolean(getTenantSession()));
  
  // Maintenance request dialog
  const [maintenanceDialog, setMaintenanceDialog] = useState(false);
  const [editMaintenanceDialog, setEditMaintenanceDialog] = useState(false);
  const [editingRequest, setEditingRequest] = useState(null);
  const [newRequest, setNewRequest] = useState({
    title: '',
    description: '',
    priority: 'medium',
    category: 'general',
    photos: []
  });

  // Payment dialog
  const [paymentDialog, setPaymentDialog] = useState(false);
  const [paymentAmount, setPaymentAmount] = useState('');
  
  // Payment edit/delete dialogs
  const [editPaymentDialog, setEditPaymentDialog] = useState(false);
  const [editingPayment, setEditingPayment] = useState(null);
  const [deletePaymentDialog, setDeletePaymentDialog] = useState(false);
  const [paymentToDelete, setPaymentToDelete] = useState(null);

  // Change password
  const [changePasswordDialog, setChangePasswordDialog] = useState(false);
  const [changePasswordForm, setChangePasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [changePasswordLoading, setChangePasswordLoading] = useState(false);
  const [changePasswordError, setChangePasswordError] = useState('');

  // Handle login - defined early so it can be used in useEffect
  const handleLogin = (tenantData) => {
    setIsAuthenticated(true);
    setTenant(tenantData);
    fetchTenantData(tenantData.id);
  };

  const resetPortal = () => {
    setTenant(null);
    setIsAuthenticated(false);
    setMaintenanceRequests([]);
    setPayments([]);
    setLease(null);
    setNotifications([]);
    setMessages([]);
  };

  // Restore a saved tenant session on page load
  useEffect(() => {
    if (!getTenantSession()) {
      setLoading(false);
      return;
    }
    api.get('/api/tenant-portal/session')
      .then((response) => {
        handleLogin({
          id: response.data.tenantId,
          email: response.data.email,
          name: tenantDisplayName(response.data)
        });
      })
      .catch(() => {
        setLoading(false);
      })
      .finally(() => setRestoringSession(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // A 401 from any tenant call ends the session (expired, signed out elsewhere, password reset)
  useEffect(() => {
    const onSessionEnded = () => {
      resetPortal();
      setLoading(false);
      setSnackbar({ open: true, message: 'Your session has ended. Please sign in again.', severity: 'info' });
    };
    window.addEventListener(TENANT_SESSION_ENDED_EVENT, onSessionEnded);
    return () => window.removeEventListener(TENANT_SESSION_ENDED_EVENT, onSessionEnded);
  }, []);

  // Refetch maintenance requests when tenant data is loaded
  useEffect(() => {
    if (tenant?.id) {
      fetchMaintenanceRequests();
    }
  }, [tenant?.id]);

  // fetchTenantData is called after login, not on mount

  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown date';
    try {
      // Handle Firestore timestamp objects
      if (typeof dateString === 'object' && dateString._seconds) {
        const date = new Date(dateString._seconds * 1000);
        return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
      }
      // Handle regular date strings
      const date = new Date(dateString);
      return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
    } catch (error) {
      return 'Invalid date';
    }
  };

  const formatDateOnly = (dateString) => {
    if (!dateString) return 'Unknown date';
    try {
      // Handle Firestore timestamp objects
      if (typeof dateString === 'object' && dateString._seconds) {
        const date = new Date(dateString._seconds * 1000);
        return date.toLocaleDateString();
      }
      // Handle regular date strings
      const date = new Date(dateString);
      return date.toLocaleDateString();
    } catch (error) {
      return 'Invalid date';
    }
  };

  const fetchMessages = async (tenantId) => {
    try {
      const response = await api.get(`/api/tenant-portal/tenant/${tenantId}/messages`);
      setMessages(response.data);
    } catch (error) {
      console.error('❌ Error fetching messages:', error);
      // Don't show error snackbar - not critical
    }
  };

  const fetchMaintenanceRequests = async () => {
    try {
      if (!tenant?.id) {
        console.log('⚠️ No tenant ID available for fetching maintenance requests');
        return;
      }
      
      console.log('🔍 Fetching maintenance requests for tenant:', tenant.id);
      const response = await api.get(`/api/tenant-portal/tenant/${tenant.id}/maintenance-requests`);
      console.log('📊 Received maintenance requests:', response.data.length);
      setMaintenanceRequests(response.data);
    } catch (error) {
      console.error('Error fetching maintenance requests:', error);
      setSnackbar({ open: true, message: 'Failed to fetch maintenance requests', severity: 'error' });
    }
  };

  const fetchPayments = async (tenantId) => {
    try {
      console.log('🔍 Fetching payments for tenant:', tenantId);
      console.log('📍 API endpoint:', `/api/tenant-portal/tenant/${tenantId}/payments`);
      const response = await api.get(`/api/tenant-portal/tenant/${tenantId}/payments`);
      console.log('📊 Received payments:', response.data.length);
      console.log('📊 Payment data:', response.data);
      setPayments(response.data);
      console.log('✅ Payments state updated');
    } catch (error) {
      console.error('❌ Error fetching payments:', error);
      setSnackbar({ open: true, message: 'Failed to fetch payments', severity: 'error' });
    }
  };

  const fetchNotifications = async (tenantId) => {
    try {
      console.log('🔍 Fetching notifications for tenant:', tenantId);
      const response = await api.get(`/api/tenant-portal/tenant/${tenantId}/notifications`);
      console.log('📊 Received notifications:', response.data.length);
      setNotifications(response.data);
    } catch (error) {
      console.error('❌ Error fetching notifications:', error);
      setSnackbar({ open: true, message: 'Failed to fetch notifications', severity: 'error' });
    }
  };

  const fetchLease = async (tenantId) => {
    try {
      console.log('🔍 Fetching lease for tenant:', tenantId);
      const response = await api.get(`/api/tenant-portal/tenant/${tenantId}/lease`);
      console.log('📊 Received lease data:', response.data);
      
      // Map lease data from leases collection to expected format
      const leaseData = response.data;
      setLease({
        startDate: leaseData.startDate || 'N/A',
        endDate: leaseData.endDate || 'N/A',
        rent: leaseData.monthlyRent || 0,
        deposit: leaseData.securityDeposit || 0,
        status: leaseData.status || 'active',
        terms: leaseData.notes || 'No additional terms specified'
      });
    } catch (error) {
      console.error('❌ Error fetching lease:', error);
      setSnackbar({ open: true, message: 'Failed to fetch lease information', severity: 'error' });
    }
  };

  const fetchTenantData = async (tenantId) => {
    try {
      if (!tenantId) {
        console.error('❌ No tenant ID provided to fetchTenantData');
        setLoading(false);
        return;
      }
      
      const response = await api.get(`/api/tenant-portal/tenant/${tenantId}`);
      const tenantData = response.data;
      const { propertyId, unitId } = tenantData;
      const propertyName = tenantData.propertyName || 'Unknown Property';
      const unitName = tenantData.unitName || 'Unknown Unit';

      setTenant({
        id: tenantData.id,
        name: tenantDisplayName(tenantData),
        email: tenantData.email,
        phone: tenantData.phone || 'N/A',
        property: propertyName,
        unit: unitName,
        propertyId: propertyId,
        unitId: unitId,
        rent: tenantData.leaseInfo?.monthlyRent || tenantData.leaseInfo?.rentAmount || tenantData.rent || 0,
        dueDate: tenantData.dueDate || 'N/A',
        status: tenantData.status || 'active'
      });
      
      // Lease data will be fetched from the dedicated leases collection
      // This ensures we get the most up-to-date lease information
      
      // Fetch real maintenance requests from API
      await fetchMaintenanceRequests();

      // Fetch real payments from API
      await fetchPayments(tenantData.id);

      // Fetch real notifications from API
      await fetchNotifications(tenantData.id);

      await fetchMessages(tenantData.id);

      // Fetch real lease data from API
      await fetchLease(tenantData.id);
      
      setLoading(false);
      setIsAuthenticated(true);

    } catch (error) {
      console.error('❌ Error fetching tenant data:', error);
      setSnackbar({ open: true, message: 'Failed to load tenant data', severity: 'error' });
      setLoading(false);
    }
  };

  const handleMaintenanceSubmit = async () => {
    try {

      // Upload photos first if any
      let uploadedPhotos = [];
      let failedPhotos = 0;
      if (newRequest.photos && newRequest.photos.length > 0) {
        console.log('📸 Uploading photos...');
        for (const photo of newRequest.photos) {
          if (photo.file) {
            try {
              const formData = await photoFormData(photo.file);
              const uploadResponse = await api.post(`/api/tenant-portal/tenant/${tenant.id}/photos`, formData, {
                timeout: PHOTO_UPLOAD_TIMEOUT_MS
              });

              console.log('✅ Photo uploaded:', uploadResponse.data);
              // Add only Firestore-safe properties
              uploadedPhotos.push({
                id: uploadResponse.data.id || null,
                url: uploadResponse.data.url || '',
                filename: uploadResponse.data.filename || ''
              });
            } catch (uploadError) {
              failedPhotos += 1;
              console.error('❌ Error uploading photo:', uploadError);
              // Continue with other photos even if one fails
            }
          } else if (photo.url) {
            // Photo already uploaded (existing photo) - clean to only include Firestore-safe properties
            uploadedPhotos.push({
              id: photo.id || null,
              url: photo.url || '',
              filename: photo.filename || ''
            });
          }
        }
      }

      // Make API call to backend with uploaded photo URLs
      const response = await api.post(`/api/tenant-portal/tenant/${tenant.id}/maintenance-requests`, {
        title: newRequest.title,
        description: newRequest.description,
        priority: newRequest.priority,
        category: newRequest.category,
        photos: uploadedPhotos
      });

      console.log('✅ Maintenance request submitted:', response.data);

      // Refresh maintenance requests from API to get the latest data
      await fetchMaintenanceRequests();
      setMaintenanceDialog(false);
      setNewRequest({ title: '', description: '', priority: 'medium', category: 'general', photos: [] });
      setSnackbar(failedPhotos
        ? { open: true, message: `Request submitted, but ${failedPhotos} photo(s) didn't upload. Check your connection and add them by editing the request.`, severity: 'warning' }
        : { open: true, message: 'Maintenance request submitted successfully!', severity: 'success' });
    } catch (error) {
      console.error('❌ Error submitting maintenance request:', error);
      setSnackbar({ open: true, message: 'Failed to submit request', severity: 'error' });
    }
  };

  const handleEditRequest = (request) => {
    setEditingRequest(request);
    setEditMaintenanceDialog(true);
  };

  const handleUpdateRequest = async () => {
    try {
      console.log('📝 Updating maintenance request:', editingRequest);

      // Upload new photos if any and collect all photos
      let uploadedPhotos = [];
      let failedPhotos = 0;
      
      // First, collect existing photos (filter out any with file property - those are pending uploads)
      if (editingRequest.photos && editingRequest.photos.length > 0) {
        for (const photo of editingRequest.photos) {
          // If photo has a file, it needs to be uploaded first
          if (photo.file) {
            try {
              const formData = await photoFormData(photo.file);
              const uploadResponse = await api.post(`/api/tenant-portal/tenant/${tenant.id}/photos`, formData, {
                timeout: PHOTO_UPLOAD_TIMEOUT_MS
              });

              console.log('✅ Photo uploaded:', uploadResponse.data);
              // Add only Firestore-safe properties
              uploadedPhotos.push({
                id: uploadResponse.data.id || null,
                url: uploadResponse.data.url || '',
                filename: uploadResponse.data.filename || ''
              });
            } catch (uploadError) {
              failedPhotos += 1;
              console.error('❌ Error uploading photo:', uploadError);
            }
          } else {
            // Existing photo - clean it to only include Firestore-safe properties
            uploadedPhotos.push({
              id: photo.id || null,
              url: photo.url || '',
              filename: photo.filename || ''
            });
          }
        }
      }

      // Update the request
      const response = await api.put(`/api/tenant-portal/tenant/${tenant.id}/maintenance-requests/${editingRequest.id}`, {
        title: editingRequest.title,
        description: editingRequest.description,
        priority: editingRequest.priority,
        category: editingRequest.category,
        photos: uploadedPhotos
      });

      console.log('✅ Maintenance request updated:', response.data);

      // Refresh maintenance requests from API to get the latest data
      await fetchMaintenanceRequests();

      setEditMaintenanceDialog(false);
      setEditingRequest(null);
      setSnackbar(failedPhotos
        ? { open: true, message: `Request updated, but ${failedPhotos} photo(s) didn't upload. Check your connection and try again.`, severity: 'warning' }
        : { open: true, message: 'Maintenance request updated successfully!', severity: 'success' });
    } catch (error) {
      console.error('❌ Error updating maintenance request:', error);
      setSnackbar({ open: true, message: error.response?.data?.error || 'Failed to update request', severity: 'error' });
    }
  };

  const handleDeleteRequest = async (requestId) => {
    if (!window.confirm('Are you sure you want to delete this maintenance request?')) return;
    
    try {
      console.log('🗑️ Deleting maintenance request:', requestId);
      
      await api.delete(`/api/tenant-portal/tenant/${tenant.id}/maintenance-requests/${requestId}`);

      // Refresh maintenance requests from API to get the latest data
      await fetchMaintenanceRequests();

      setSnackbar({ open: true, message: 'Maintenance request deleted successfully!', severity: 'success' });
    } catch (error) {
      console.error('❌ Error deleting maintenance request:', error);
      setSnackbar({ open: true, message: error.response?.data?.error || 'Failed to delete request', severity: 'error' });
    }
  };


  const handleRegister = (tenantData) => {
    console.log('🔐 Registration successful, tenant data:', tenantData);
    // Fetch full tenant data using the registered tenant's ID
    fetchTenantData(tenantData.id);
  };

  const handleLogout = async () => {
    try {
      await api.post('/api/tenant-portal/logout');
    } catch (error) {
      console.error('❌ Sign-out request failed:', error);
    }
    clearTenantSession();
    resetPortal();
  };

  const handleForgotPassword = () => {
    // Optional callback - TenantAuth shows success message in the form
  };

  const handleChangePassword = async () => {
    setChangePasswordError('');
    if (changePasswordForm.newPassword !== changePasswordForm.confirmPassword) {
      setChangePasswordError('New passwords do not match');
      return;
    }
    const passwordError = tenantPasswordError(changePasswordForm.newPassword);
    if (passwordError) {
      setChangePasswordError(passwordError);
      return;
    }
    setChangePasswordLoading(true);
    try {
      await api.post('/api/tenant-invitations/change-password', {
        currentPassword: changePasswordForm.currentPassword,
        newPassword: changePasswordForm.newPassword
      });
      setChangePasswordDialog(false);
      setChangePasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setSnackbar({ open: true, message: 'Password changed successfully', severity: 'success' });
    } catch (err) {
      setChangePasswordError(err.response?.data?.error || 'Failed to change password');
    } finally {
      setChangePasswordLoading(false);
    }
  };

  const handlePaymentSubmit = async () => {
    try {
      console.log('💰 Submitting payment:', paymentAmount, 'for tenant:', tenant?.id);

      // Make API call to backend to save the payment
      const response = await api.post(`/api/tenant-portal/tenant/${tenant.id}/payments`, {
        amount: parseFloat(paymentAmount),
        method: 'Online',
        description: 'Rent payment'
      });

      console.log('✅ Payment submitted:', response.data);

      // Refresh payments list to get the latest data from backend
      console.log('🔄 Refreshing payments list...');
      const paymentsResponse = await api.get(`/api/tenant-portal/tenant/${tenant.id}/payments`);
      console.log('📊 Refreshed payments:', paymentsResponse.data);
      setPayments(paymentsResponse.data);

      setPaymentDialog(false);
      setPaymentAmount('');
      setSnackbar({ open: true, message: 'Payment submitted successfully!', severity: 'success' });
    } catch (error) {
      console.error('❌ Error submitting payment:', error);
      setSnackbar({ open: true, message: 'Failed to submit payment', severity: 'error' });
    }
  };

  const handleEditPayment = (payment) => {
    setEditingPayment({ ...payment });
    setEditPaymentDialog(true);
  };

  const handleUpdatePayment = async () => {
    try {
      console.log('💰 Updating payment:', editingPayment);

      const updateData = {
        amount: parseFloat(editingPayment.amount),
        method: editingPayment.method || 'Online',
        description: editingPayment.description || 'Rent payment'
      };

      await api.put(`/api/tenant-portal/tenant/${tenant.id}/payments/${editingPayment.id}`, updateData);
      
      console.log('✅ Payment updated successfully');
      
      // Refresh payments list
      console.log('🔄 Refreshing payments list...');
      const paymentsResponse = await api.get(`/api/tenant-portal/tenant/${tenant.id}/payments`);
      console.log('📊 Refreshed payments:', paymentsResponse.data);
      setPayments(paymentsResponse.data);
      
      setEditPaymentDialog(false);
      setEditingPayment(null);
      setSnackbar({ open: true, message: 'Payment updated successfully!', severity: 'success' });
    } catch (error) {
      console.error('❌ Error updating payment:', error);
      setSnackbar({ open: true, message: error.response?.data?.error || 'Failed to update payment', severity: 'error' });
    }
  };

  const handleDeletePaymentClick = (payment) => {
    setPaymentToDelete(payment);
    setDeletePaymentDialog(true);
  };

  const handleDeletePayment = async () => {
    try {
      console.log('🗑️ Deleting payment:', paymentToDelete.id);
      
      await api.delete(`/api/tenant-portal/tenant/${tenant.id}/payments/${paymentToDelete.id}`);
      
      console.log('✅ Payment deleted successfully');
      
      // Refresh payments list
      console.log('🔄 Refreshing payments list...');
      const paymentsResponse = await api.get(`/api/tenant-portal/tenant/${tenant.id}/payments`);
      console.log('📊 Refreshed payments:', paymentsResponse.data);
      setPayments(paymentsResponse.data);
      
      setDeletePaymentDialog(false);
      setPaymentToDelete(null);
      setSnackbar({ open: true, message: 'Payment deleted successfully!', severity: 'success' });
    } catch (error) {
      console.error('❌ Error deleting payment:', error);
      setSnackbar({ open: true, message: error.response?.data?.error || 'Failed to delete payment', severity: 'error' });
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'success';
      case 'in-progress': return 'primary';
      case 'pending': return 'warning';
      default: return 'default';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'error';
      case 'medium': return 'warning';
      case 'low': return 'success';
      default: return 'default';
    }
  };

  if (restoringSession) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><Typography>Loading...</Typography></Box>;
  }

  if (!isAuthenticated) {
    return (
      <Box>
        <TenantAuth
          onLogin={handleLogin}
          onRegister={handleRegister}
          onForgotPassword={handleForgotPassword}
        />
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    );
  }

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><Typography>Loading...</Typography></Box>;
  }

  return (
    <Box sx={{ mt: { xs: 2, sm: 4 }, p: { xs: 1.5, sm: 2, md: 3 } }}>
      {/* Header */}
      <Card sx={{ mb: { xs: 2, sm: 3 } }}>
        <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
          <Box sx={{ 
            display: 'flex', 
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { xs: 'flex-start', sm: 'center' }, 
            mb: { xs: 1.5, sm: 2 },
            gap: { xs: 1.5, sm: 0 }
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', flex: 1, width: { xs: '100%', sm: 'auto' } }}>
              <Avatar sx={{ mr: { xs: 1.5, sm: 2 }, bgcolor: 'primary.main', width: { xs: 40, sm: 56 }, height: { xs: 40, sm: 56 } }}>
                {tenant?.name?.charAt(0)}
              </Avatar>
              <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                <Typography 
                  variant="h5"
                  sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' }, mb: { xs: 0.5, sm: 0 } }}
                >
                  {tenant?.name}
                </Typography>
                <Typography 
                  variant="body2" 
                  color="text.secondary"
                  sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
                >
                  {isMobile ? `${tenant?.property}` : `${tenant?.property} - Unit ${tenant?.unit}`}
                </Typography>
              </Box>
            </Box>
            <Button
              variant="outlined"
              size={isMobile ? 'small' : 'small'}
              onClick={handleLogout}
              fullWidth={isMobile}
            >
              Logout
            </Button>
          </Box>
          
          <Grid container spacing={{ xs: 1.5, sm: 2 }}>
            <Grid item xs={12} md={3}>
              <Typography variant="body2" color="text.secondary">Rent Due</Typography>
              <Typography variant="h6">${tenant?.rent}</Typography>
            </Grid>
            <Grid item xs={12} md={3}>
              <Typography variant="body2" color="text.secondary">Due Date</Typography>
              <Typography variant="h6">{tenant?.dueDate}</Typography>
            </Grid>
            <Grid item xs={12} md={3}>
              <Typography variant="body2" color="text.secondary">Status</Typography>
              <Chip label={tenant?.status} color="success" size="small" />
            </Grid>
            <Grid item xs={12} md={3}>
              <Typography variant="body2" color="text.secondary">Contact</Typography>
              <Typography variant="body2">{tenant?.email}</Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Navigation Tabs */}
      <Tabs 
        value={activeTab} 
        onChange={(e, newValue) => setActiveTab(newValue)} 
        sx={{ mb: { xs: 2, sm: 3 } }}
        variant={isMobile ? 'scrollable' : 'standard'}
        scrollButtons="auto"
      >
        <Tab icon={<HomeIcon />} iconPosition={isMobile ? 'top' : 'start'} label={isMobile ? "Home" : "Dashboard"} />
        <Tab icon={<BuildIcon />} iconPosition={isMobile ? 'top' : 'start'} label="Maintenance" />
        <Tab icon={<PaymentIcon />} iconPosition={isMobile ? 'top' : 'start'} label="Payments" />
        <Tab icon={<MailIcon />} iconPosition={isMobile ? 'top' : 'start'} label="Messages" />
        <Tab icon={<LeaseIcon />} iconPosition={isMobile ? 'top' : 'start'} label="Lease" />
        <Tab icon={<NotificationIcon />} iconPosition={isMobile ? 'top' : 'start'} label={isMobile ? "Alerts" : "Notifications"} />
        <Tab icon={<SettingsIcon />} iconPosition={isMobile ? 'top' : 'start'} label="Settings" />
      </Tabs>

      {/* Dashboard Tab */}
      {activeTab === 0 && (
        <Grid container spacing={{ xs: 2, sm: 3 }}>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                <Typography 
                  variant="h6" 
                  gutterBottom
                  sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' }, mb: { xs: 1.5, sm: 2 } }}
                >
                  Recent Maintenance Requests
                </Typography>
                <List>
                  {maintenanceRequests.slice(0, 3).map((request) => (
                    <ListItem key={request.id}>
                      <ListItemIcon>
                        <BuildIcon color="primary" />
                      </ListItemIcon>
                      <ListItemText
                        primary={request.title}
                        secondary={`Status: ${request.status}`}
                      />
                      <Chip
                        label={request.priority}
                        color={getPriorityColor(request.priority)}
                        size="small"
                      />
                    </ListItem>
                  ))}
                </List>
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={() => setMaintenanceDialog(true)}
                  sx={{ mt: { xs: 1.5, sm: 2 } }}
                  fullWidth={isMobile}
                  size={isMobile ? 'small' : 'medium'}
                >
                  New Request
                </Button>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card>
              <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                <Typography 
                  variant="h6" 
                  gutterBottom
                  sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' }, mb: { xs: 1.5, sm: 2 } }}
                >
                  Recent Payments
                </Typography>
                <List>
                  {payments.slice(0, 3).map((payment) => (
                    <ListItem key={payment.id}>
                      <ListItemIcon>
                        <PaymentIcon color="success" />
                      </ListItemIcon>
                      <ListItemText
                        primary={`$${payment.amount}`}
                        secondary={`${formatDateOnly(payment.date)} - ${payment.method}`}
                      />
                      <Chip
                        label={payment.status}
                        color={getStatusColor(payment.status)}
                        size="small"
                      />
                    </ListItem>
                  ))}
                </List>
                <Button
                  variant="outlined"
                  startIcon={<PaymentIcon />}
                  onClick={() => setPaymentDialog(true)}
                  sx={{ mt: { xs: 1.5, sm: 2 } }}
                  fullWidth={isMobile}
                  size={isMobile ? 'small' : 'medium'}
                >
                  Make Payment
                </Button>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Maintenance Tab */}
      {activeTab === 1 && (
        <Box>
          <Box sx={{ 
            display: 'flex', 
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: 'space-between', 
            alignItems: { xs: 'flex-start', sm: 'center' }, 
            mb: { xs: 2, sm: 3 },
            gap: { xs: 1.5, sm: 0 }
          }}>
            <Typography 
              variant="h5"
              sx={{ fontSize: { xs: '1.5rem', sm: '1.75rem' } }}
            >
              Maintenance Requests
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setMaintenanceDialog(true)}
              fullWidth={isMobile}
              size={isMobile ? 'medium' : 'medium'}
            >
              New Request
            </Button>
          </Box>

          <Grid container spacing={{ xs: 1.5, sm: 2 }}>
            {maintenanceRequests.map((request) => (
              <Grid item xs={12} key={request.id}>
                <Card>
                  <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                    <Box sx={{ 
                      display: 'flex', 
                      flexDirection: { xs: 'column', sm: 'row' },
                      justifyContent: 'space-between', 
                      alignItems: { xs: 'flex-start', sm: 'flex-start' },
                      gap: { xs: 1.5, sm: 0 }
                    }}>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography 
                          variant="h6"
                          sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' }, mb: { xs: 0.75, sm: 1 } }}
                        >
                          {request.title}
                        </Typography>
                        <Typography 
                          variant="body2" 
                          color="text.secondary" 
                          sx={{ mb: { xs: 0.75, sm: 1 }, fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
                        >
                          {request.description}
                        </Typography>
                        <Typography 
                          variant="caption" 
                          color="text.secondary"
                          sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                        >
                          Submitted: {formatDateOnly(request.createdAt)}
                        </Typography>
                        
                        {/* Display photos if any */}
                        {request.photos && request.photos.length > 0 && (
                          <Box sx={{ mt: 2 }}>
                            <Typography variant="subtitle2" gutterBottom>
                              Photos ({request.photos.length})
                            </Typography>
                            <Grid container spacing={1}>
                              {request.photos.slice(0, 3).map((photo, index) => {
                                console.log('🔍 TenantPortal Photo object structure:', { 
                                  index, 
                                  photo: photo,
                                  photoStringified: JSON.stringify(photo, null, 2),
                                  hasUrl: !!photo.url, 
                                  urlType: typeof photo.url,
                                  urlValue: photo.url,
                                  hasId: !!photo.id,
                                  idType: typeof photo.id,
                                  idValue: photo.id,
                                  apiBaseURL: api.defaults.baseURL
                                });
                                
                                // Handle different photo URL formats
                                let imageUrl = null;
                                if (photo.url && photo.url.startsWith('http')) {
                                  // Full URL (from Firebase Storage)
                                  imageUrl = photo.url;
                                } else if (photo.url) {
                                  // Relative URL (append to API base URL)
                                  imageUrl = `${api.defaults.baseURL}${photo.url}`;
                                } else if (photo.preview && photo.preview.startsWith('data:')) {
                                  // Base64 preview URL (local preview)
                                  imageUrl = photo.preview;
                                }
                                console.log('🖼️ TenantPortal Rendering photo:', { id: photo.id, url: photo.url, preview: photo.preview, imageUrl, filename: photo.filename });
                                
                                // Skip photos without valid URLs
                                if (!imageUrl) {
                                  console.log('⚠️ TenantPortal Skipping photo without valid URL:', photo);
                                  return null;
                                }
                                
                                return (
                                  <Grid item xs={4} key={photo.id || index}>
                                    <Card sx={{ height: 80, overflow: 'hidden' }}>
                                      <img
                                        src={imageUrl}
                                        alt={`Photo ${index + 1}`}
                                        style={{
                                          width: '100%',
                                          height: '100%',
                                          objectFit: 'cover'
                                        }}
                                        onLoad={() => {
                                          console.log('✅ TenantPortal Photo loaded successfully:', imageUrl);
                                        }}
                                        onError={(e) => {
                                          console.error('❌ TenantPortal Error loading photo:', imageUrl, e);
                                          e.target.style.display = 'none';
                                        }}
                                      />
                                    </Card>
                                  </Grid>
                                );
                              })}
                            </Grid>
                            {request.photos.length > 3 && (
                              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                                +{request.photos.length - 3} more photos
                              </Typography>
                            )}
                          </Box>
                        )}
                      </Box>
                      <Box sx={{ 
                        display: 'flex', 
                        flexDirection: { xs: 'row', sm: 'column' },
                        alignItems: { xs: 'flex-start', sm: 'flex-end' }, 
                        gap: { xs: 1, sm: 1 },
                        flexWrap: 'wrap'
                      }}>
                        {canTenantChangeRequest(request) && (
                        <Box sx={{ display: 'flex', gap: { xs: 0.5, sm: 0.5 } }}>
                          <Tooltip title="Edit Request">
                            <IconButton
                              size={isMobile ? 'small' : 'small'}
                              onClick={() => handleEditRequest(request)}
                              sx={{ color: 'primary.main' }}
                            >
                              <EditIcon fontSize={isMobile ? 'small' : 'small'} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete Request">
                            <IconButton
                              size={isMobile ? 'small' : 'small'}
                              onClick={() => handleDeleteRequest(request.id)}
                              sx={{ color: 'error.main' }}
                            >
                              <DeleteIcon fontSize={isMobile ? 'small' : 'small'} />
                            </IconButton>
                          </Tooltip>
                        </Box>
                        )}
                        <Box sx={{ display: 'flex', gap: { xs: 0.5, sm: 0.5 }, flexWrap: 'wrap' }}>
                          <Chip
                            label={request.status}
                            color={getStatusColor(request.status)}
                            size="small"
                            sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                          />
                          <Chip
                            label={request.priority}
                            color={getPriorityColor(request.priority)}
                            size="small"
                            sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                          />
                        </Box>
                      </Box>
                    </Box>
                    {request.assignedTo && (
                      <Box sx={{ mt: 2, p: 1, bgcolor: 'grey.100', borderRadius: 1 }}>
                        <Typography variant="body2">
                          <strong>Assigned to:</strong> {request.assignedTo}
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      {/* Payments Tab */}
      {activeTab === 2 && (
        <Box>
          {isFinancialsEnabled() && tenant?.id && (tenant.propertyId || tenant.leaseInfo?.propertyId) && (
            <TenantPortalLedger
              tenantId={tenant.id}
              propertyId={tenant.propertyId || tenant.leaseInfo?.propertyId}
            />
          )}
          <Box sx={{ 
            display: 'flex', 
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: 'space-between', 
            alignItems: { xs: 'flex-start', sm: 'center' }, 
            mb: { xs: 2, sm: 3 },
            gap: { xs: 1.5, sm: 0 }
          }}>
            <Typography 
              variant="h5"
              sx={{ fontSize: { xs: '1.5rem', sm: '1.75rem' } }}
            >
              Payment History
            </Typography>
            <Button
              variant="contained"
              startIcon={<PaymentIcon />}
              onClick={() => setPaymentDialog(true)}
              fullWidth={isMobile}
              size={isMobile ? 'medium' : 'medium'}
            >
              Make Payment
            </Button>
          </Box>

          <Grid container spacing={{ xs: 1.5, sm: 2 }}>
            {payments.map((payment) => (
              <Grid item xs={12} md={6} key={payment.id}>
                <Card>
                  <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                    <Box sx={{ 
                      display: 'flex', 
                      flexDirection: { xs: 'column', sm: 'row' },
                      justifyContent: 'space-between', 
                      alignItems: { xs: 'flex-start', sm: 'center' },
                      gap: { xs: 1.5, sm: 0 }
                    }}>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography 
                          variant="h6"
                          sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' }, mb: { xs: 0.5, sm: 0.5 } }}
                        >
                          ${payment.amount}
                        </Typography>
                        <Typography 
                          variant="body2" 
                          color="text.secondary"
                          sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
                        >
                          {formatDateOnly(payment.date)} - {payment.method}
                        </Typography>
                      </Box>
                      <Box sx={{ 
                        display: 'flex', 
                        flexDirection: { xs: 'row', sm: 'column' },
                        alignItems: { xs: 'flex-start', sm: 'flex-end' }, 
                        gap: { xs: 1, sm: 1 },
                        flexWrap: 'wrap'
                      }}>
                        {canTenantChangePayment(payment) && (
                        <Box sx={{ display: 'flex', gap: { xs: 0.5, sm: 0.5 } }}>
                          <Tooltip title="Edit Payment">
                            <IconButton
                              size={isMobile ? 'small' : 'small'}
                              onClick={() => handleEditPayment(payment)}
                              sx={{ color: 'primary.main' }}
                            >
                              <EditIcon fontSize={isMobile ? 'small' : 'small'} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete Payment">
                            <IconButton
                              size={isMobile ? 'small' : 'small'}
                              onClick={() => handleDeletePaymentClick(payment)}
                              sx={{ color: 'error.main' }}
                            >
                              <DeleteIcon fontSize={isMobile ? 'small' : 'small'} />
                            </IconButton>
                          </Tooltip>
                        </Box>
                        )}
                        <Chip
                          label={payment.status}
                          color={getStatusColor(payment.status)}
                          size="small"
                          sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                        />
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      {/* Messages Tab */}
      {activeTab === 3 && (
        <Box>
          <Typography 
            variant="h5" 
            gutterBottom
            sx={{ fontSize: { xs: '1.5rem', sm: '1.75rem' }, mb: { xs: 1.5, sm: 2 } }}
          >
            My Messages
          </Typography>
          <Alert severity="info" sx={{ mb: { xs: 2, sm: 3 }, fontSize: { xs: '0.875rem', sm: '1rem' } }}>
            <Typography variant="body2" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
              View all messages sent to you by property management.
            </Typography>
          </Alert>

          <Grid container spacing={{ xs: 1.5, sm: 2 }}>
            {messages.length === 0 ? (
              <Grid item xs={12}>
                <Card>
                  <CardContent>
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                      <MailIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
                      <Typography variant="h6" color="textSecondary">
                        No messages yet
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        You haven't received any messages from property management.
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ) : (
              messages.map((message) => (
                <Grid item xs={12} key={message.id}>
                  <Card>
                    <CardContent>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                        <Box sx={{ flexGrow: 1 }}>
                          <Typography variant="h6">{message.subject}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {message.createdAt ? formatDate(message.createdAt) : 'Unknown date'}
                          </Typography>
                        </Box>
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<VisibilityIcon />}
                          onClick={() => {
                            setSelectedMessage(message);
                            setMessageDialog(true);
                          }}
                        >
                          Read
                        </Button>
                      </Box>
                      <Typography variant="body2" color="text.secondary">
                        {message.body ? (message.body.length > 100 ? message.body.substring(0, 100) + '...' : message.body) : 'No preview available'}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))
            )}
          </Grid>
        </Box>
      )}

      {/* Lease Tab */}
      {activeTab === 4 && (
        <Card>
          <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
            <Typography 
              variant="h5" 
              gutterBottom
              sx={{ fontSize: { xs: '1.5rem', sm: '1.75rem' }, mb: { xs: 1.5, sm: 2 } }}
            >
              Lease Information
            </Typography>
            <Grid container spacing={{ xs: 2, sm: 3 }}>
              <Grid item xs={12} md={6}>
                <Typography variant="h6" gutterBottom>Property & Unit</Typography>
                <List>
                  <ListItem>
                    <ListItemText
                      primary="Property"
                      secondary={tenant?.property || 'N/A'}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="Unit"
                      secondary={tenant?.unit || 'N/A'}
                    />
                  </ListItem>
                </List>
                <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>Lease Details</Typography>
                <List>
                  <ListItem>
                    <ListItemText
                      primary="Lease Term"
                      secondary={`${lease?.startDate} to ${lease?.endDate}`}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="Monthly Rent"
                      secondary={`$${lease?.rent || 0}`}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="Security Deposit"
                      secondary={`$${lease?.deposit || 0}`}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="Status"
                      secondary={
                        <Chip label={lease?.status || 'N/A'} color="success" size="small" />
                      }
                    />
                  </ListItem>
                </List>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="h6" gutterBottom>Terms & Conditions</Typography>
                <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                  <Typography variant="body2">
                    {lease?.terms}
                  </Typography>
                </Paper>
                <Button
                  variant="outlined"
                  sx={{ mt: 2 }}
                  startIcon={<LeaseIcon />}
                >
                  Download Lease PDF
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* Notifications Tab */}
      {activeTab === 5 && (
        <Card>
          <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
            <Typography 
              variant="h5" 
              gutterBottom
              sx={{ fontSize: { xs: '1.5rem', sm: '1.75rem' }, mb: { xs: 1.5, sm: 2 } }}
            >
              Notifications
            </Typography>
            <List>
              {notifications.map((notification) => (
                <ListItem key={notification.id}>
                  <ListItemIcon>
                    {notification.type === 'payment' ? (
                      <PaymentIcon color="info" />
                    ) : notification.type === 'maintenance' ? (
                      <BuildIcon color="primary" />
                    ) : (
                      <InfoIcon color="info" />
                    )}
                  </ListItemIcon>
                  <ListItemText
                    primary={notification.title}
                    secondary={notification.message}
                  />
                  <Typography variant="caption" color="text.secondary">
                    {formatDateOnly(notification.createdAt)}
                  </Typography>
                </ListItem>
              ))}
            </List>
          </CardContent>
        </Card>
      )}

      {/* Settings Tab */}
      {activeTab === 6 && (
        <Card>
          <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
            <Typography 
              variant="h5" 
              gutterBottom
              sx={{ fontSize: { xs: '1.5rem', sm: '1.75rem' }, mb: { xs: 1.5, sm: 2 } }}
            >
              Account Settings
            </Typography>
            <Grid container spacing={{ xs: 2, sm: 3 }}>
              <Grid item xs={12} md={6}>
                <Typography variant="h6" gutterBottom>Personal Information</Typography>
                <TextField
                  fullWidth
                  label="Name"
                  value={tenant?.name}
                  margin="normal"
                />
                <TextField
                  fullWidth
                  label="Email"
                  value={tenant?.email}
                  margin="normal"
                />
                <TextField
                  fullWidth
                  label="Phone"
                  value={tenant?.phone}
                  margin="normal"
                />
                <Button variant="contained" sx={{ mt: 2 }}>
                  Update Information
                </Button>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="h6" gutterBottom>Notification Preferences</Typography>
                <FormControlLabel
                  control={
                    <Switch
                      checked={notificationPreferences.emailNotifications}
                      onChange={(e) => setNotificationPreferences({
                        ...notificationPreferences,
                        emailNotifications: e.target.checked
                      })}
                    />
                  }
                  label="Email Notifications"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={notificationPreferences.smsNotifications}
                      onChange={(e) => setNotificationPreferences({
                        ...notificationPreferences,
                        smsNotifications: e.target.checked
                      })}
                    />
                  }
                  label="SMS Notifications"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={notificationPreferences.maintenanceUpdates}
                      onChange={(e) => setNotificationPreferences({
                        ...notificationPreferences,
                        maintenanceUpdates: e.target.checked
                      })}
                    />
                  }
                  label="Maintenance Updates"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={notificationPreferences.paymentReminders}
                      onChange={(e) => setNotificationPreferences({
                        ...notificationPreferences,
                        paymentReminders: e.target.checked
                      })}
                    />
                  }
                  label="Payment Reminders"
                />
                <Button variant="outlined" sx={{ mt: 2 }}>
                  Save Preferences
                </Button>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>Change Password</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Enter your current password and choose a new one.
                </Typography>
                <Button variant="contained" color="primary" onClick={() => setChangePasswordDialog(true)}>
                  Change Password
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* Change Password Dialog */}
      <Dialog open={changePasswordDialog} onClose={() => { setChangePasswordDialog(false); setChangePasswordError(''); }} maxWidth="sm" fullWidth>
        <DialogTitle>Change Password</DialogTitle>
        <DialogContent>
          {changePasswordError && (
            <Alert severity="error" sx={{ mb: 2 }}>{changePasswordError}</Alert>
          )}
          <TextField
            fullWidth
            label="Current Password"
            type="password"
            value={changePasswordForm.currentPassword}
            onChange={(e) => setChangePasswordForm({ ...changePasswordForm, currentPassword: e.target.value })}
            margin="normal"
            required
            autoComplete="current-password"
          />
          <TextField
            fullWidth
            label="New Password"
            type="password"
            value={changePasswordForm.newPassword}
            onChange={(e) => setChangePasswordForm({ ...changePasswordForm, newPassword: e.target.value })}
            margin="normal"
            required
            helperText={TENANT_PASSWORD_HINT}
            autoComplete="new-password"
          />
          <TextField
            fullWidth
            label="Confirm New Password"
            type="password"
            value={changePasswordForm.confirmPassword}
            onChange={(e) => setChangePasswordForm({ ...changePasswordForm, confirmPassword: e.target.value })}
            margin="normal"
            required
            autoComplete="new-password"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setChangePasswordDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleChangePassword} disabled={changePasswordLoading}>
            {changePasswordLoading ? 'Updating...' : 'Update Password'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog 
        open={maintenanceDialog} 
        onClose={() => setMaintenanceDialog(false)} 
        maxWidth="md" 
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>Submit Maintenance Request</DialogTitle>
        <DialogContent sx={{ p: { xs: 1.5, sm: 2 } }}>
          <TextField
            fullWidth
            label="Issue Title"
            value={newRequest.title}
            onChange={(e) => setNewRequest({ ...newRequest, title: e.target.value })}
            margin="normal"
          />
          <TextField
            fullWidth
            label="Description"
            value={newRequest.description}
            onChange={(e) => setNewRequest({ ...newRequest, description: e.target.value })}
            margin="normal"
            multiline
            rows={4}
          />
          <TextField
            fullWidth
            select
            label="Priority"
            value={newRequest.priority}
            onChange={(e) => setNewRequest({ ...newRequest, priority: e.target.value })}
            margin="normal"
          >
            <MenuItem value="low">Low</MenuItem>
            <MenuItem value="medium">Medium</MenuItem>
            <MenuItem value="high">High</MenuItem>
          </TextField>
          <TextField
            fullWidth
            select
            label="Category"
            value={newRequest.category}
            onChange={(e) => setNewRequest({ ...newRequest, category: e.target.value })}
            margin="normal"
          >
            <MenuItem value="general">General</MenuItem>
            <MenuItem value="plumbing">Plumbing</MenuItem>
            <MenuItem value="electrical">Electrical</MenuItem>
            <MenuItem value="hvac">HVAC</MenuItem>
            <MenuItem value="appliance">Appliance</MenuItem>
            <MenuItem value="structural">Structural</MenuItem>
            <MenuItem value="pest">Pest Control</MenuItem>
            <MenuItem value="other">Other</MenuItem>
          </TextField>
          <PhotoUpload
            key={maintenanceDialog ? 'open' : 'closed'}
            onPhotosChange={(photos) => setNewRequest({ ...newRequest, photos })}
            maxPhotos={5}
            existingPhotos={newRequest.photos || []}
          />
        </DialogContent>
        <DialogActions sx={{ p: { xs: 1.5, sm: 2 }, flexDirection: { xs: 'column-reverse', sm: 'row' }, gap: { xs: 1, sm: 0 } }}>
          <Button 
            onClick={() => setMaintenanceDialog(false)}
            fullWidth={isMobile}
            size={isMobile ? 'medium' : 'medium'}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleMaintenanceSubmit} 
            variant="contained"
            fullWidth={isMobile}
            size={isMobile ? 'medium' : 'medium'}
          >
            Submit Request
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Maintenance Request Dialog */}
      <Dialog 
        open={editMaintenanceDialog} 
        onClose={() => setEditMaintenanceDialog(false)} 
        maxWidth="md" 
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>Edit Maintenance Request</DialogTitle>
        <DialogContent sx={{ p: { xs: 1.5, sm: 2 } }}>
          <TextField
            fullWidth
            label="Issue Title"
            value={editingRequest?.title || ''}
            onChange={(e) => setEditingRequest({ ...editingRequest, title: e.target.value })}
            margin="normal"
          />
          <TextField
            fullWidth
            label="Description"
            value={editingRequest?.description || ''}
            onChange={(e) => setEditingRequest({ ...editingRequest, description: e.target.value })}
            margin="normal"
            multiline
            rows={4}
          />
          <TextField
            fullWidth
            select
            label="Priority"
            value={editingRequest?.priority || 'medium'}
            onChange={(e) => setEditingRequest({ ...editingRequest, priority: e.target.value })}
            margin="normal"
          >
            <MenuItem value="low">Low</MenuItem>
            <MenuItem value="medium">Medium</MenuItem>
            <MenuItem value="high">High</MenuItem>
            <MenuItem value="urgent">Urgent</MenuItem>
          </TextField>
          <TextField
            fullWidth
            select
            label="Category"
            value={editingRequest?.category || 'general'}
            onChange={(e) => setEditingRequest({ ...editingRequest, category: e.target.value })}
            margin="normal"
          >
            <MenuItem value="general">General</MenuItem>
            <MenuItem value="plumbing">Plumbing</MenuItem>
            <MenuItem value="electrical">Electrical</MenuItem>
            <MenuItem value="hvac">HVAC</MenuItem>
            <MenuItem value="appliance">Appliance</MenuItem>
            <MenuItem value="structural">Structural</MenuItem>
            <MenuItem value="pest">Pest Control</MenuItem>
            <MenuItem value="other">Other</MenuItem>
          </TextField>
          <PhotoUpload
            onPhotosChange={(photos) => setEditingRequest({ ...editingRequest, photos })}
            maxPhotos={5}
            existingPhotos={editingRequest?.photos || []}
          />
        </DialogContent>
        <DialogActions sx={{ p: { xs: 1.5, sm: 2 }, flexDirection: { xs: 'column-reverse', sm: 'row' }, gap: { xs: 1, sm: 0 } }}>
          <Button 
            onClick={() => setEditMaintenanceDialog(false)}
            fullWidth={isMobile}
            size={isMobile ? 'medium' : 'medium'}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleUpdateRequest} 
            variant="contained"
            fullWidth={isMobile}
            size={isMobile ? 'medium' : 'medium'}
          >
            Update Request
          </Button>
        </DialogActions>
      </Dialog>

      {/* Payment Dialog */}
      <Dialog 
        open={paymentDialog} 
        onClose={() => setPaymentDialog(false)}
        fullScreen={isMobile}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>Make Payment</DialogTitle>
        <DialogContent sx={{ p: { xs: 1.5, sm: 2 } }}>
          <TextField
            fullWidth
            label="Payment Amount"
            type="number"
            value={paymentAmount}
            onChange={(e) => setPaymentAmount(e.target.value)}
            margin="normal"
            InputProps={{
              startAdornment: <Typography>$</Typography>
            }}
          />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Payment will be processed securely through our payment system.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: { xs: 1.5, sm: 2 }, flexDirection: { xs: 'column-reverse', sm: 'row' }, gap: { xs: 1, sm: 0 } }}>
          <Button 
            onClick={() => setPaymentDialog(false)}
            fullWidth={isMobile}
            size={isMobile ? 'medium' : 'medium'}
          >
            Cancel
          </Button>
          <Button 
            onClick={handlePaymentSubmit} 
            variant="contained"
            fullWidth={isMobile}
            size={isMobile ? 'medium' : 'medium'}
          >
            Submit Payment
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Payment Dialog */}
      <Dialog 
        open={editPaymentDialog} 
        onClose={() => setEditPaymentDialog(false)}
        fullScreen={isMobile}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>Edit Payment</DialogTitle>
        <DialogContent sx={{ p: { xs: 1.5, sm: 2 } }}>
          <TextField
            fullWidth
            label="Payment Amount"
            type="number"
            value={editingPayment?.amount || ''}
            onChange={(e) => setEditingPayment({ ...editingPayment, amount: e.target.value })}
            margin="normal"
            InputProps={{
              startAdornment: <Typography>$</Typography>
            }}
          />
          <TextField
            fullWidth
            select
            label="Method"
            value={editingPayment?.method || 'Online'}
            onChange={(e) => setEditingPayment({ ...editingPayment, method: e.target.value })}
            margin="normal"
          >
            <MenuItem value="Online">Online</MenuItem>
            <MenuItem value="Credit Card">Credit Card</MenuItem>
            <MenuItem value="Bank Transfer">Bank Transfer</MenuItem>
            <MenuItem value="Cash">Cash</MenuItem>
          </TextField>
          <TextField
            fullWidth
            label="Description"
            value={editingPayment?.description || ''}
            onChange={(e) => setEditingPayment({ ...editingPayment, description: e.target.value })}
            margin="normal"
          />
        </DialogContent>
        <DialogActions sx={{ p: { xs: 1.5, sm: 2 }, flexDirection: { xs: 'column-reverse', sm: 'row' }, gap: { xs: 1, sm: 0 } }}>
          <Button 
            onClick={() => setEditPaymentDialog(false)}
            fullWidth={isMobile}
            size={isMobile ? 'medium' : 'medium'}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleUpdatePayment} 
            variant="contained"
            fullWidth={isMobile}
            size={isMobile ? 'medium' : 'medium'}
          >
            Update Payment
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Payment Confirmation Dialog */}
      <Dialog 
        open={deletePaymentDialog} 
        onClose={() => setDeletePaymentDialog(false)}
        fullScreen={isMobile}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>Delete Payment</DialogTitle>
        <DialogContent sx={{ p: { xs: 1.5, sm: 2 } }}>
          <Typography sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
            Are you sure you want to delete this payment of ${paymentToDelete?.amount}?
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: { xs: 1.5, sm: 2 }, flexDirection: { xs: 'column-reverse', sm: 'row' }, gap: { xs: 1, sm: 0 } }}>
          <Button 
            onClick={() => setDeletePaymentDialog(false)}
            fullWidth={isMobile}
            size={isMobile ? 'medium' : 'medium'}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleDeletePayment} 
            variant="contained" 
            color="error"
            fullWidth={isMobile}
            size={isMobile ? 'medium' : 'medium'}
          >
            Delete Payment
          </Button>
        </DialogActions>
      </Dialog>

      {/* Message Dialog */}
      <Dialog 
        open={messageDialog} 
        onClose={() => setMessageDialog(false)} 
        maxWidth="md" 
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
          {selectedMessage?.subject || 'Message'}
        </DialogTitle>
        <DialogContent sx={{ p: { xs: 1.5, sm: 2 } }}>
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" color="text.secondary">
              {selectedMessage?.createdAt ? formatDate(selectedMessage.createdAt) : 'Unknown date'}
            </Typography>
          </Box>
          <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
            {selectedMessage?.body || 'No message content available.'}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: { xs: 1.5, sm: 2 }, flexDirection: { xs: 'column-reverse', sm: 'row' }, gap: { xs: 1, sm: 0 } }}>
          <Button 
            onClick={() => setMessageDialog(false)}
            fullWidth={isMobile}
            size={isMobile ? 'medium' : 'medium'}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        TransitionComponent={undefined}
        disablePortal={true}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default TenantPortal; 